/**
 * @file     PanelPrimitives.tsx
 * @purpose  Shared visual primitives for slug-or-key chart panels so font
 *           weights, sizes, letter-spacing, colors, and surface treatment
 *           stay consistent across every section. Replaces ~300 inline
 *           style declarations across the 7 chart panels.
 *
 *           Use:
 *             <PanelCard>
 *               <PanelHeader
 *                 eyebrow="Inventory mix"
 *                 title="What this area trades in"
 *                 subtitle="North Vancouver · 1,000 listings"
 *                 right={<Chip ... />}
 *               />
 *               {chart}
 *               <PanelSourceLine>Statistics Canada · 2021 Census</PanelSourceLine>
 *             </PanelCard>
 */
import { layoutTokens } from '@/design-system/tokens/layout';
import { forwardRef, type CSSProperties, type ReactNode, type Ref } from 'react';
import { useTheme, alpha, type Theme } from '@mui/material/styles';

// ─── card surface ────────────────────────────────────────────────────────────
interface PanelCardProps {
  children: ReactNode;
  /** Override padding (defaults '24px 28px'). MyRead uses '28px 28px 24px'. */
  padding?: CSSProperties['padding'];
  /** Optional gradient overlay (MyRead lens band uses this). */
  backgroundGradient?: string;
  /** Extra shadow layer (MyRead). */
  extraShadow?: string;
  /** Pass through ref + extra style for analytics/animation wrappers. */
  style?: CSSProperties;
  className?: string;
}

export const PanelCard = forwardRef(function PanelCard(
  { children, padding = '24px 28px', backgroundGradient, extraShadow, style, className }: PanelCardProps,
  ref: Ref<HTMLElement>,
) {
  const theme = useTheme();
  const baseShadow = `0 1px 2px ${alpha(theme.palette.common.black, 0.04)}`;
  return (
    <section
      ref={ref}
      className={className}
      style={{
        padding,
        borderRadius: layoutTokens.radius.lg,
        background: backgroundGradient ?? theme.palette.background.paper,
        border: `1px solid ${alpha(theme.palette.divider, 1)}`,
        boxShadow: extraShadow ? `${baseShadow}, ${extraShadow}` : baseShadow,
        ...style,
      }}
    >
      {children}
    </section>
  );
});

/** Hook helper for panels that wrap in framer-motion sections — returns
 *  the same surface treatment PanelCard uses so the style can be spread
 *  into motion.section's `style` prop without restructuring. */
export function usePanelCardStyle(opts?: {
  padding?: CSSProperties['padding'];
  backgroundGradient?: string;
  extraShadow?: string;
}): CSSProperties {
  const theme = useTheme();
  const baseShadow = `0 1px 2px ${alpha(theme.palette.common.black, 0.04)}`;
  return {
    padding: opts?.padding ?? '24px 28px',
    borderRadius: layoutTokens.radius.lg,
    background: opts?.backgroundGradient ?? theme.palette.background.paper,
    border: `1px solid ${alpha(theme.palette.divider, 1)}`,
    boxShadow: opts?.extraShadow ? `${baseShadow}, ${opts.extraShadow}` : baseShadow,
  };
}

// ─── typographic primitives ──────────────────────────────────────────────────

const eyebrowStyle = (theme: Theme): CSSProperties => ({
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: 1.2,
  textTransform: 'uppercase',
  color: theme.palette.text.secondary,
});

const titleStyle = (theme: Theme): CSSProperties => ({
  fontSize: 18,
  fontWeight: 600,
  color: theme.palette.text.primary,
  letterSpacing: -0.3,
  marginTop: 8,
});

const subtitleStyle = (theme: Theme): CSSProperties => ({
  fontSize: 12.5,
  color: theme.palette.text.secondary,
  marginTop: 4,
  lineHeight: 1.55,
  maxWidth: 720,
});

const sourceStyle = (theme: Theme): CSSProperties => ({
  marginTop: 16,
  fontSize: 11,
  color: theme.palette.text.secondary,
  opacity: 0.8,
  letterSpacing: 0.3,
});

export function PanelEyebrow({ children, id, style }: { children: ReactNode; id?: string; style?: CSSProperties }) {
  const theme = useTheme();
  return <span id={id} style={{ ...eyebrowStyle(theme), ...style }}>{children}</span>;
}

export function PanelTitle({ children, as: As = 'div', id, style }: { children: ReactNode; as?: 'div' | 'h2' | 'h3'; id?: string; style?: CSSProperties }) {
  const theme = useTheme();
  return <As id={id} style={{ ...titleStyle(theme), ...style }}>{children}</As>;
}

export function PanelSubtitle({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  const theme = useTheme();
  return <div style={{ ...subtitleStyle(theme), ...style }}>{children}</div>;
}

export function PanelSourceLine({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  const theme = useTheme();
  return <div style={{ ...sourceStyle(theme), ...style }}>{children}</div>;
}

// ─── composed header ────────────────────────────────────────────────────────
interface PanelHeaderProps {
  eyebrow?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  /** Right-side adornment (chips, freshness pills, etc.). */
  right?: ReactNode;
  /** Bottom margin under the header block. */
  marginBottom?: number | string;
}

export function PanelHeader({ eyebrow, title, subtitle, right, marginBottom = 12 }: PanelHeaderProps) {
  return (
    <header
      style={{
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        gap: 12,
        marginBottom,
        flexWrap: 'wrap',
      }}
    >
      <div style={{ minWidth: 0, flex: '1 1 auto' }}>
        {eyebrow && <PanelEyebrow>{eyebrow}</PanelEyebrow>}
        <PanelTitle>{title}</PanelTitle>
        {subtitle && <PanelSubtitle>{subtitle}</PanelSubtitle>}
      </div>
      {right && <div style={{ flex: '0 0 auto' }}>{right}</div>}
    </header>
  );
}
